import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Modal } from '~/ui/modal/Modal';
import { Overlay } from '~/ui/overlay/Overlay';
// Removed Button import - using custom styled buttons
import NarrationPopup from '~/editor/narration/NarrationPopup';
import { CloseButton } from '~/ui/buttons/CloseButton';
import AddAudioMicIcon from '~/assets/button/addAudioMic.svg';
import HotspotSymbolSpeakerIcon from '~/assets/icons/hotspot-symbol-speaker.svg';
import HotspotSymbolPlusIcon from '~/assets/icons/hotspot-symbol-plus.svg';

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-bottom: 24px;

  .closeBtn {
    position: static;
    top: auto;
    left: auto;
    margin: 0;
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }
`;

const Title = styled.h2`
  flex: 1;
  min-width: 0;
  margin: 0;
  color: var(--Colors-Neutrial-Grey-5, #2e2e2e);
  font-variant-numeric: lining-nums proportional-nums;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
  text-align: left;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-family: Raleway;
  font-weight: 500;
  font-size: 14px;
  color: #6b7280;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: Raleway;
  font-size: 14px;
  height: 44px;
  box-sizing: border-box;
  background: #ffffff;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #9845fb;
    box-shadow: 0 0 0 3px rgba(152, 69, 251, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const AddAudioButton = styled.button`
  display: flex;
  height: 38px;
  padding: 8px var(--20, 20px) 8px var(--Spacing-spacing-lg, 20px);
  justify-content: center;
  align-items: center;
  gap: var(--Spacing-spacing-xxsm, 4px);
  align-self: stretch;
  width: 100%;
  background: #f8f1ff;
  color: #9738ff;
  border: none;
  border-radius: var(--8, 8px);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  margin-bottom: 20px;
  box-sizing: border-box;
  transition: background 0.2s ease;

  &:hover {
    background: #ede5ff;
  }

  &:active {
    transform: translateY(1px);
  }

  .icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const AudioPreview = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f8f1ff;
  border-radius: 8px;
  border: 1px solid #e0d4ff;

  .audio-info {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;

    .name {
      font-family: Raleway;
      font-weight: 600;
      font-size: 14px;
      color: #333;
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const RemoveAudioButton = styled.button`
  background: #f3ecff;
  border: none;
  color: #9738ff;
  cursor: pointer;
  font-size: 12px;
  padding: 12px 16px;
  border-radius: 4px;
  font-family: Raleway;
  font-weight: 600;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  width: 100%;
  margin-bottom: 16px;
  display: block;

  &:hover {
    background: #9738ff;
    color: #fff;
  }

  &:active {
    transform: translateY(1px);
  }

  audio {
    width: 100%;
    height: 32px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
  width: 100%;
`;

const CancelButton = styled.button`
  display: flex;
  height: 38px;
  padding: 8px var(--20, 20px) 8px var(--Spacing-spacing-lg, 20px);
  justify-content: center;
  align-items: center;
  gap: var(--Spacing-spacing-xxsm, 4px);
  background: #f8f1ff;
  color: #9738ff;
  border: none;
  border-radius: var(--8, 8px);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  box-sizing: border-box;
  transition: background 0.2s ease;

  &:hover {
    background: #ede5ff;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const SubmitButton = styled.button`
  display: flex;
  height: 38px;
  padding: 8px var(--20, 20px) 8px var(--Spacing-spacing-lg, 20px);
  justify-content: center;
  align-items: center;
  gap: var(--Spacing-spacing-xxsm, 4px);
  background: #9738ff;
  color: #ffffff;
  border: none;
  border-radius: var(--8, 8px);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  box-sizing: border-box;
  transition: background 0.2s ease;

  &:hover {
    background: #8532e8;
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
    transform: none;
  }
`;

const DisplayDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #d4d4d4;
  margin: 4px 0 16px;
`;

const DisplayRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-bottom: 8px;
`;

const DisplayLabel = styled.span`
  color: var(--Colors-Neutrial-Grey-5, #2e2e2e);
  font-variant-numeric: lining-nums proportional-nums;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
`;

const DisplayControls = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
`;

const DisplayToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  padding: 4px;
  background: #f1f1f1;
  border-radius: 8px;
  flex-shrink: 0;
`;

const DisplayToggleOption = styled.button<{ $active?: boolean }>`
  display: flex;
  height: 34px;
  padding: ${props =>
    props.$active
      ? 'var(--8, 8px) var(--Spacing-spacing-xsm, 8px)'
      : 'var(--Spacing-spacing-xxsm, 4px) var(--Spacing-spacing-sm, 12px)'};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border: none;
  border-radius: 4px;
  background: ${props => (props.$active ? '#ffffff' : 'transparent')};
  box-shadow: ${props =>
    props.$active ? '0 1px 2px rgba(0, 0, 0, 0.06)' : 'none'};
  color: var(--Colors-Neutrial-Grey-5, #2e2e2e);
  font-variant-numeric: lining-nums proportional-nums;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: ${props => (props.$active ? 600 : 400)};
  line-height: 20px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    background: ${props =>
      props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'};
  }
`;

const SymbolPreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  font-family: Raleway;
  font-weight: 700;
  font-size: 22px;
  line-height: 1;
  color: #000000;
  user-select: none;
`;

const SymbolSpeakerIcon = styled(HotspotSymbolSpeakerIcon)`
  width: 25px;
  height: 18px;
  color: #000000;
  flex-shrink: 0;
`;

const SymbolPlusIcon = styled(HotspotSymbolPlusIcon)`
  width: 17px;
  height: 17px;
  color: #2e2e2e;
  flex-shrink: 0;
`;

const NumberInput = styled.input`
  width: 96px;
  height: 38px;
  padding: 8px 12px;
  border: 1px solid #a9a9a9;
  border-radius: 4px;
  font-family: Raleway;
  font-size: 14px;
  line-height: 1.5;
  color: #2e2e2e;
  background: #ffffff;
  box-sizing: border-box;
  flex-shrink: 0;

  &:focus {
    outline: none;
    border-color: #9845fb;
    box-shadow: 0 0 0 3px rgba(152, 69, 251, 0.1);
  }

  &::placeholder {
    color: #6f6f6f;
  }
`;

type DisplayMode = 'symbol' | 'number';

interface AudioItem {
  id: number;
  mediaUrl: string;
  originalFilename?: string;
  name?: string;
}

interface AddTextAudioModalProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    text: string;
    body: string;
    audio: AudioItem | null;
    coordinates: { x: number; y: number };
    iconType: 'icon' | 'number';
    iconValue: number | null;
  }) => void;
  clickPosition: { x: number; y: number } | null;
  editingData?: {
    text: string;
    body?: string;
    audio: AudioItem | null;
    iconType?: string | null;
    iconValue?: number | null;
  };
}

const AddTextAudioModal: React.FC<AddTextAudioModalProps> = ({
  title = 'Add text and audio',
  isOpen,
  onClose,
  onSubmit,
  clickPosition,
  editingData
}) => {
  const [text, setText] = useState('');
  const [body, setBody] = useState('');
  const [selectedAudio, setSelectedAudio] = useState<AudioItem | null>(null);
  const [showAudioSelector, setShowAudioSelector] = useState(false);
  const [displayMode, setDisplayMode] = useState<DisplayMode>('symbol');
  const [displayNumber, setDisplayNumber] = useState('');

  // Initialize form with editing data when available
  React.useEffect(() => {
    if (editingData) {
      setText(editingData.text != null ? String(editingData.text) : '');
      setBody(editingData.body != null ? String(editingData.body) : '');
      if (editingData.audio) {
        setSelectedAudio(editingData.audio);
      } else {
        setSelectedAudio(null);
      }

      if (editingData.iconType === 'number') {
        setDisplayMode('number');
        setDisplayNumber(
          editingData.iconValue != null ? String(editingData.iconValue) : ''
        );
      } else {
        // 'icon' or unset — default to Symbol in the UI
        setDisplayMode('symbol');
        setDisplayNumber('');
      }
    } else {
      setText('');
      setBody('');
      setSelectedAudio(null);
      setDisplayMode('symbol');
      setDisplayNumber('');
    }
  }, [editingData, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const textStr = typeof text === 'string' ? text : String(text ?? '');
    const bodyStr = typeof body === 'string' ? body : String(body ?? '');

    if (!textStr.trim()) {
      alert('Please enter some text');
      return;
    }

    if (!clickPosition) {
      alert('No position data available');
      return;
    }

    const iconType: 'icon' | 'number' =
      displayMode === 'number' ? 'number' : 'icon';
    let iconValue: number | null = null;

    if (iconType === 'number') {
      const parsed = parseInt(displayNumber, 10);
      if (!Number.isFinite(parsed) || parsed < 1 || parsed > 99) {
        alert('Please enter a number between 1 and 99');
        return;
      }
      iconValue = parsed;
    }

    onSubmit({
      text: textStr.trim(),
      body: bodyStr.trim(),
      audio: selectedAudio,
      coordinates: clickPosition,
      iconType,
      iconValue
    });

    handleClose();
  };

  const handleClose = () => {
    setText('');
    setBody('');
    setSelectedAudio(null);
    setShowAudioSelector(false);
    setDisplayMode('symbol');
    setDisplayNumber('');
    onClose();
  };

  const handleAudioSelect = (audio: AudioItem) => {
    setSelectedAudio(audio);
    setShowAudioSelector(false);
  };

  const handleRemoveAudio = () => {
    setSelectedAudio(null);
  };

  const parsedDisplayNumber = parseInt(displayNumber, 10);
  const isNumberMarkerInvalid =
    displayMode === 'number' &&
    (!Number.isFinite(parsedDisplayNumber) ||
      parsedDisplayNumber < 1 ||
      parsedDisplayNumber > 99);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      <Overlay open={isOpen} opacity={0.5} onClick={handleClose}>
        <div onClick={e => e.stopPropagation()}>
          <Modal
            open={true}
            width="400px"
            horizontal="center"
            vertical="center"
            padding="16px 24px"
            borderRadius="20px"
          >
            <ModalHeader>
              <Title>{title}</Title>
              <CloseButton
                type="close"
                onClick={handleClose}
                autoFocus={false}
              />
            </ModalHeader>
            <form onSubmit={handleSubmit}>
              {/* Position info removed as requested */}

              <FormGroup>
                <Label htmlFor="text">Title</Label>
                <TextInput
                  id="text"
                  type="text"
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder="Enter your text here..."
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="body">Body</Label>
                <TextInput
                  id="body"
                  type="text"
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  placeholder="Enter your body here..."
                />
              </FormGroup>

              {selectedAudio ? (
                <>
                  <RemoveAudioButton
                    type="button"
                    onClick={handleRemoveAudio}
                    title="Remove audio"
                  >
                    Remove Audio
                  </RemoveAudioButton>
                  <AudioPreview>
                    <div className="audio-info"></div>
                    {selectedAudio.mediaUrl && (
                      <audio controls preload="metadata">
                        {selectedAudio.mediaUrl && (
                          <source src={selectedAudio.mediaUrl} />
                        )}
                      </audio>
                    )}
                  </AudioPreview>
                </>
              ) : (
                <AddAudioButton
                  type="button"
                  onClick={() => setShowAudioSelector(true)}
                >
                  <AddAudioMicIcon className="icon" aria-hidden="true" />
                  Add Audio
                </AddAudioButton>
              )}

              <DisplayDivider />

              <DisplayRow>
                <DisplayLabel>Display:</DisplayLabel>
                <DisplayControls>
                  <DisplayToggle role="tablist" aria-label="Display type">
                    <DisplayToggleOption
                      type="button"
                      role="tab"
                      aria-selected={displayMode === 'symbol'}
                      $active={displayMode === 'symbol'}
                      onClick={() => setDisplayMode('symbol')}
                    >
                      Symbol
                    </DisplayToggleOption>
                    <DisplayToggleOption
                      type="button"
                      role="tab"
                      aria-selected={displayMode === 'number'}
                      $active={displayMode === 'number'}
                      onClick={() => setDisplayMode('number')}
                    >
                      Number
                    </DisplayToggleOption>
                  </DisplayToggle>
                  {displayMode === 'symbol' ? (
                    <SymbolPreview aria-hidden="true">
                      {selectedAudio ? (
                        <SymbolSpeakerIcon />
                      ) : (
                        <SymbolPlusIcon />
                      )}
                    </SymbolPreview>
                  ) : (
                    <NumberInput
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={displayNumber}
                      onChange={e => {
                        const digits = e.target.value
                          .replace(/\D/g, '')
                          .slice(0, 2);
                        if (digits === '') {
                          setDisplayNumber('');
                          return;
                        }
                        const parsed = parseInt(digits, 10);
                        if (parsed > 99) {
                          setDisplayNumber('99');
                          return;
                        }
                        setDisplayNumber(digits);
                      }}
                      placeholder="# Number"
                      aria-label="Hotspot number"
                    />
                  )}
                </DisplayControls>
              </DisplayRow>

              <ButtonGroup>
                <CancelButton type="button" onClick={handleClose}>
                  Cancel
                </CancelButton>
                <SubmitButton type="submit" disabled={isNumberMarkerInvalid}>
                  Submit
                </SubmitButton>
              </ButtonGroup>
            </form>
          </Modal>
        </div>
      </Overlay>

      {/* Audio Selector Modal */}
      {showAudioSelector && (
        <NarrationPopup
          onClose={() => setShowAudioSelector(false)}
          skipUpdate={true}
          onSelectCallback={
            handleAudioSelect as unknown as (audio: {
              id?: number | string;
              mediaUrl?: string;
              [key: string]: unknown;
            }) => void
          }
          skipSequenceNarration={true}
        />
      )}
    </>,
    document.body
  );
};

export default AddTextAudioModal;
